import React from 'react';
import BlogPost from '../BlogPost';

const BlogSIRSComponentList: React.FC = () => {
  return (
    <BlogPost
      title="The HB 913 Structural Integrity Reserve Study Component List: Plain-English Guide for Florida Condo Boards"
      description="A plain-English breakdown of the 8 mandatory SIRS components under HB 913, what each one means, and how they connect to your baseline funding calculations."
      publishDate="May 2026"
      readTime="10 min read"
    >
      <p>
        Your engineering firm just delivered a 35-page structural integrity reserve study.
        Page four has a table with eight categories, dozens of line items, replacement cost
        estimates, and a column labelled "remaining useful life." You have a board meeting
        in three weeks and need to present a funding plan.
      </p>
      <p>
        Where do you start?
      </p>
      <p>
        This guide breaks down the mandatory structural integrity reserve study component
        list under Florida's HB 913, explains what each item actually means in plain English,
        and connects each component directly to the baseline funding calculations your
        association is required to produce. No engineering degree required.
      </p>

      <h2>Why the Component List Matters More Than You Think</h2>
      <p>
        The eight SIRS components are not just a checklist. They are the mathematical
        inputs to your entire 30-year baseline funding plan. Get the component inventory
        wrong — miss an item, misread a replacement cost, or use the wrong remaining
        useful life figure — and your funding projections will be wrong too.
      </p>
      <p>
        Under HB 913, a baseline funding plan must demonstrate that your reserve cash
        balance will never drop below zero over a 30-year projection period. If your
        component list is incomplete or inaccurate, your plan will either under-project
        costs (creating a false sense of security) or over-project them (causing
        unnecessary assessment increases). Both outcomes hurt your owners.
      </p>
      <p>
        Accurate component data is not a technicality. It is the foundation of every
        financial decision your board will make about reserve contributions for the
        next three decades.
      </p>

      <h2>The 8 Mandatory SIRS Components Under Florida Law</h2>
      <p>
        Florida Statute §718.112(2)(g) defines the specific components that every
        structural integrity reserve study must evaluate. Here is each one in plain
        language, with notes on what boards commonly get wrong.
      </p>

      <h3>Component 1: Roof</h3>
      <p>
        The roof system includes the roof covering (membrane, tile, shingles), the
        roof deck, any waterproofing layer, and associated flashings. Your SIRS report
        will typically list the roof as a single line item with a total replacement
        cost and an estimated remaining useful life in years.
      </p>
      <p>
        <strong>Common board mistake:</strong> Assuming the roof replacement cost in
        the SIRS is the same number as the last contractor quote you received. Engineering
        firms use standardised cost databases that may differ from current local market
        pricing. Your baseline funding plan must use the SIRS figure — but your board
        should be aware of the difference if significant.
      </p>
      <p>
        <strong>What to look for in your report:</strong> Replacement cost (total, not
        per unit), year of last replacement, estimated remaining useful life (typically
        15–25 years for flat membrane roofs in South Florida's climate).
      </p>

      <h3>Component 2: Load-Bearing Walls and Primary Structural Members</h3>
      <p>
        This covers the structural skeleton of your building: load-bearing walls,
        columns, beams, and the primary structural systems that hold the building
        up. In concrete high-rises — which represent the majority of South Florida
        coastal condos — this means reinforced concrete members and post-tensioned
        slabs.
      </p>
      <p>
        <strong>Common board mistake:</strong> Treating this as a cosmetic category.
        Structural concrete repair and remediation is typically the largest single
        line item in a South Florida SIRS, often running into the millions for older
        coastal buildings where chloride intrusion has caused rebar corrosion. Do
        not underestimate this number.
      </p>
      <p>
        <strong>What to look for in your report:</strong> The engineer may list concrete
        restoration, post-tensioning cable replacement, and structural steel items
        separately. Add them all together for your baseline funding purposes —
        they all belong to this category.
      </p>

      <h3>Component 3: Fire Protection Systems</h3>
      <p>
        This includes fire sprinkler systems, fire alarm systems, standpipes, fire
        extinguishers, and related suppression equipment. Life safety systems have
        mandated inspection and replacement cycles that create predictable future costs.
      </p>
      <p>
        <strong>Common board mistake:</strong> Confusing the annual inspection cost
        (which belongs in your operating budget, not your reserves) with the system
        replacement cost (which belongs in reserves). Your SIRS should capture the
        full system replacement cost, not the routine maintenance cost.
      </p>
      <p>
        <strong>What to look for in your report:</strong> Sprinkler system replacement,
        alarm panel replacement, and any scheduled code-compliance upgrades. Useful life
        on fire suppression systems typically ranges from 25–50 years depending on
        system type and local code requirements.
      </p>

      <h3>Component 4: Plumbing</h3>
      <p>
        The plumbing component covers common-area plumbing systems: main supply lines,
        drainage stacks, sewer laterals, and domestic hot water systems in common areas.
        Individual unit plumbing is generally not a SIRS item unless it affects shared
        structural elements.
      </p>
      <p>
        <strong>Common board mistake:</strong> Older buildings often have cast iron or
        galvanised steel drain stacks that are approaching the end of their useful life.
        This is frequently one of the most under-funded reserve items in Florida
        associations. Your SIRS engineer will identify the condition and estimate
        replacement timing.
      </p>
      <p>
        <strong>What to look for in your report:</strong> Drain stack lining or
        replacement, main supply line replacement, and sewer lateral condition.
        Buildings constructed before 1990 should pay particular attention to
        this section.
      </p>

      <h3>Component 5: Electrical Systems</h3>
      <p>
        This covers the common-area electrical infrastructure: the main distribution
        panel, branch circuit panels in common areas, wiring in common spaces, elevators'
        electrical components, and any electrical systems serving the building's shared
        functions. It does not include individual unit electrical panels.
      </p>
      <p>
        <strong>Common board mistake:</strong> Elevators are a significant electrical
        cost item that many boards track separately under an elevator maintenance
        contract. Confirm with your engineer whether elevator electrical components
        are captured in the electrical section, the elevator section, or both.
        Duplication or omission of elevator costs is a common SIRS error.
      </p>
      <p>
        <strong>What to look for in your report:</strong> Main electrical panel
        replacement, common area wiring condition, and any code-upgrade requirements
        (especially important in buildings that predate modern electrical codes).
      </p>

      <h3>Component 6: Waterproofing and Exterior Painting</h3>
      <p>
        This is one of the most frequently recurring SIRS items. In Florida's coastal
        climate, exterior coatings and waterproofing systems degrade rapidly from UV
        exposure, salt air, and hurricane-driven moisture. The exterior painting cycle
        is typically 5–10 years; waterproofing membrane systems run 15–25 years.
      </p>
      <p>
        <strong>Common board mistake:</strong> Treating exterior painting as a cosmetic
        operating expense rather than a structural protection cost. Waterproofing
        failure leads directly to structural concrete damage (Component 2). The two
        are connected — which is why HB 913 includes waterproofing as a mandated
        structural component, not just an aesthetic one.
      </p>
      <p>
        <strong>What to look for in your report:</strong> Exterior painting cycle costs
        and remaining time before next required painting, waterproofing membrane
        replacement cost and condition rating, sealant and caulking replacement schedules.
      </p>

      <h3>Component 7: Windows and Exterior Doors</h3>
      <p>
        This includes all exterior windows, sliding glass doors, and entry doors that
        serve as part of the building envelope. In South Florida, this almost always
        means impact-rated systems meeting current Florida Building Code wind load
        requirements.
      </p>
      <p>
        <strong>Common board mistake:</strong> Assuming current windows are grandfathered
        indefinitely. When windows reach the end of their useful life and must be replaced,
        they must be replaced to current code — which means impact-rated glazing at
        current prices. The replacement cost for an older building upgrading from
        non-impact to impact-rated windows is substantially higher than a like-for-like
        swap. Your SIRS engineer should be accounting for this.
      </p>
      <p>
        <strong>What to look for in your report:</strong> Total window/door replacement
        cost, remaining useful life, and whether the cost estimate includes code-upgrade
        costs or just like-for-like replacement.
      </p>

      <h3>Component 8: Any Item Over $25,000 Affecting Structural Integrity</h3>
      <p>
        This is the catch-all category. HB 913 raised the reserve threshold from $10,000
        to $25,000 (indexed to inflation annually). Any common-area component with a
        replacement cost exceeding $25,000 whose failure would affect the structural
        integrity of one of the seven categories above must be included in the SIRS.
      </p>
      <p>
        <strong>Common items that appear here:</strong> Elevators (structural mechanism
        not covered in electrical), parking structure elements (if separate from the
        main building), seawall and dock structures (for waterfront communities), and
        pool decks and structural waterproofing for rooftop amenity areas.
      </p>
      <p>
        <strong>Common board mistake:</strong> Assuming the engineer captured everything.
        Review the catch-all section carefully. If your building has significant items
        — a parking garage with known structural issues, a seawall approaching its end
        of life, or a pool deck requiring major remediation — and those items are not
        in the SIRS, talk to your engineer before adopting the plan.
      </p>

      <h2>How Components Connect to Your Baseline Funding Calculations</h2>
      <p>
        Every component in your SIRS feeds into your baseline funding plan through
        three numbers:
      </p>
      <ol>
        <li><strong>Replacement cost</strong> — how much it will cost to replace the component</li>
        <li><strong>Remaining useful life</strong> — how many years before replacement is needed</li>
        <li><strong>Inflation-adjusted future cost</strong> — the replacement cost adjusted for construction inflation over the remaining useful life</li>
      </ol>
      <p>
        Your baseline funding plan must ensure that the reserve fund collects enough
        contributions — starting now — so that when each component reaches the end of
        its useful life, the reserve fund can cover the replacement cost without the
        balance ever dropping below zero.
      </p>
      <p>
        This means that a roof with 8 years of remaining useful life and a $600,000
        replacement cost is a fundamentally different funding challenge than a roof
        with 18 years of remaining useful life and the same replacement cost. The
        timing of each replacement event drives your required annual contribution.
      </p>

      <h2>Red Flags to Look for in Your SIRS Report</h2>
      <p>
        Not all SIRS reports are created equal. Here are the most common issues board
        treasurers find when they review their engineering reports carefully:
      </p>
      <ul style={{lineHeight: '2rem'}}>
        <li><strong>Missing components.</strong> All 8 categories must be present. If one is absent, contact your engineer before adopting the plan.</li>
        <li><strong>Unrealistically long remaining useful life estimates.</strong> An engineer who gives a 30-year useful life to a concrete structure already showing signs of chloride intrusion is understating your risk.</li>
        <li><strong>Replacement costs that match round numbers too closely.</strong> Round-number cost estimates ($500,000 exactly for a roof, $1,000,000 for concrete restoration) may indicate broad estimates rather than detailed takeoffs. Ask your engineer what the estimate is based on.</li>
        <li><strong>No inflation adjustment.</strong> Florida's construction inflation has run above general CPI in recent years. A SIRS that uses a static 2% inflation assumption for all components may understate future costs.</li>
        <li><strong>Parking structures excluded.</strong> Under the "habitable stories" definition in HB 913, parking garages and mechanical floors may not count toward the story threshold — but structural elements of a parking structure that connect to or support habitable areas should still appear in Component 8.</li>
      </ul>

      <h2>From Component List to Funding Plan: The Next Step</h2>
      <p>
        Once you have verified that your SIRS component list is complete and accurate,
        the next step is generating your HB 913-compliant baseline funding plan. This
        is the 30-year projection showing that reserve contributions at the proposed
        rate will keep the reserve balance above zero for every single year in the
        projection period.
      </p>
      <p>
        The component list and the baseline funding plan are two halves of the same
        compliance requirement. Your engineering firm produced the first half. Your
        board is responsible for producing the second.
      </p>
    </BlogPost>
  );
};

export default BlogSIRSComponentList;
